import { usageEventService } from '@/services/usageEvent.service'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types/user'
import AdminDashboard from '@views/admin/AdminDashboard.vue'
import TranslationsView from '@views/admin/TranslationsView.vue'
import HomeView from '@views/core/HomeView.vue'
import DataBrowserView from '@views/graffiti/BrowserView.vue'
import ApiTestView from '@views/TestAPIView.vue'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@views/auth/LoginView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@views/auth/RegisterView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@views/auth/ForgotPasswordView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@views/auth/ResetPasswordView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@views/auth/VerifyEmailView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/viewer',
    name: 'viewer',
    component: () => import('@views/graffiti/viewer/ToolView.vue'),
    meta: { layout: 'ViewerLayout' },
  },
  {
    path: '/browse',
    name: 'browse',
    component: DataBrowserView,
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/news',
    name: 'news',
    component: () => import('@views/blog/NewsView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/news/:slug',
    name: 'post-detail',
    component: () => import('@views/blog/PostDetailView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/graffiti/:id',
    name: 'graffiti-detail',
    component: () => import('@views/graffiti/DetailView.vue'),
    props: true,
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/admin',
    name: 'admin',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/translations',
    name: 'admin-translations',
    component: TranslationsView,
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@views/admin/UserManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/api-test',
    name: 'admin-api-test',
    component: () => import('@views/admin/ApiTestView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/bug-reports',
    name: 'admin-bug-reports',
    component: () => import('@views/admin/BugReportListView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/bug-reports/:id',
    name: 'admin-bug-report-detail',
    component: () => import('@views/admin/BugReportDetailView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/data-backup',
    name: 'admin-data-backup',
    component: () => import('@views/admin/DataBackupView.vue'),
    meta: { requiresAuth: true, requiresRole: 'admin', layout: 'DefaultLayout' },
  },
  // Routes Researcher Dashboard
  {
    path: '/researcher/dashboard',
    name: 'researcher-dashboard',
    component: () => import('@views/researcher/ResearcherDashboard.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/users',
    name: 'researcher-users',
    component: () => import('@views/researcher/UserManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/data',
    name: 'researcher-data',
    component: () => import('@views/researcher/DataManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/data-backup',
    name: 'researcher-data-backup',
    component: () => import('@views/researcher/DataBackupView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/photos',
    name: 'researcher-photos',
    component: () => import('@views/researcher/PhotoManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/blog',
    name: 'researcher-blog',
    component: () => import('@views/researcher/BlogManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/annotation-settings',
    name: 'researcher-annotation-settings',
    component: () => import('@views/researcher/AnnotationSettingsView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/validation',
    name: 'researcher-validation',
    component: () => import('@views/researcher/DataValidationView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/admin/blog/edit/:id?',
    name: 'admin-blog-edit',
    component: () => import('@views/blog/PostEditView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/statistics',
    name: 'researcher-statistics',
    component: () => import('@views/researcher/StatisticsView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/news',
    name: 'researcher-news',
    component: () => import('@views/researcher/NewsManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/changelog',
    name: 'researcher-changelog',
    component: () => import('@views/researcher/ChangelogView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/references',
    name: 'researcher-references',
    component: () => import('@views/researcher/ReferencesManagementView.vue'),
  },
  {
    path: '/researcher/link-types',
    name: 'researcher-link-types',
    component: () => import('@views/researcher/LinkTypesManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/categories',
    name: 'researcher-categories',
    component: () => import('@views/researcher/CategoriesManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/thematiques',
    name: 'researcher-thematiques',
    component: () => import('@views/researcher/ThematiquesManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/image-collections',
    name: 'researcher-image-collections',
    component: () => import('@views/researcher/ImageCollectionsManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: ['researcher', 'admin'], layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/images',
    name: 'researcher-images',
    component: () => import('@views/researcher/ImageManagementView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/bug-reports',
    name: 'researcher-bug-reports',
    component: () => import('@views/researcher/MyBugReportsView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/bug-reports/:id',
    name: 'researcher-bug-report-detail',
    component: () => import('@views/researcher/MyBugReportDetailView.vue'),
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/researcher/translations',
    name: 'researcher-translations',
    component: TranslationsView,
    meta: { requiresAuth: true, requiresRole: 'researcher', layout: 'DefaultLayout' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@views/auth/ProfileView.vue'),
    meta: { requiresAuth: true, layout: 'DefaultLayout' },
  },
  {
    path: '/features',
    name: 'features',
    component: () => import('@views/core/FeaturesView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@views/core/AboutView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/team',
    name: 'team',
    component: () => import('@views/core/TeamView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/partners',
    name: 'partners',
    component: () => import('@views/core/PartnersView.vue'),
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/dev/api-test',
    name: 'api-test',
    component: ApiTestView,
    meta: { layout: 'DefaultLayout' },
  },
  {
    path: '/browser/theme/:themeName',
    name: 'theme-subset',
    component: () => import('@views/graffiti/subsets/ThemeView.vue'),
    meta: {
      title: 'Graffitis par thématique',
      requiresAuth: false,
    },
  },
  {
    path: '/browser/categorie/:categorieName',
    name: 'categorie-subset',
    component: () => import('@views/graffiti/subsets/CategorieView.vue'),
    props: true,
    meta: {
      title: 'Graffitis par catégorie',
      requiresAuth: false,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@views/errors/NotFoundView.vue'),
    meta: { layout: 'ErrorLayout' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Garde de navigation global
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiredRole = to.meta.requiresRole as UserRole | undefined

  if (requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]
    if (!roles.some(role => authStore.hasRole(role))) {
      return next({ name: 'not-found' }) // Ou une page 'Unauthorized'
    }
  }

  // Logger la navigation (page_view)
  const pageName = to.name?.toString() || to.path
  usageEventService.logEvent({
    eventType: 'page_view',
    targetType: 'page',
    metaStr1: pageName,
  })

  next()
})

export default router
